import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiExternalLink, FiX } from 'react-icons/fi';

/** Shared overlay modal used by the Articles / Videos sidebars.
 *
 *  Rendered through a portal into <body> so that no animated/transformed
 *  ancestor (e.g. `.page-anim`, `.anim-fade-up` cards, `:active` buttons)
 *  can become the containing block of the `position: fixed` wrapper - that
 *  is what used to push the modal off the centre of the screen. */
export default function ContentModal({ title, onClose, children, link, footer }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  if (!title && !children) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm anim-fade-in" onClick={onClose} />
      <div className="relative m-auto anim-modal flex flex-col w-full max-w-2xl max-h-[88vh] overflow-hidden rounded-2xl bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 shadow-2xl">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-gray-100 dark:border-gray-800 shrink-0">
          <h3 className="font-bold text-lg leading-snug">{title}</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-90"
            aria-label="Close"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto">{children}</div>

        {(link || footer) && (
          <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 shrink-0 flex items-center justify-between gap-3 flex-wrap">
            {link ? (
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-blue-600 hover:text-blue-700 underline break-all inline-flex items-center gap-1"
              >
                <FiExternalLink /> {link}
              </a>
            ) : <span />}
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

